import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Link,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isEmpty, uniq } from 'lodash';
import { useCallback, useMemo } from 'react';

import { AttachmentWithFile } from '@app/types/user';

export interface FileInfo {
  blob: Blob;
  name: string;
  url: string;
}

const acceptTypePdf = 'pdf';
export type ExtraExtension = typeof acceptTypePdf;

function getExtraAccept(extraExtension: ExtraExtension): string {
  switch (extraExtension) {
    case acceptTypePdf:
      return '.pdf';
    default:
      return '';
  }
}

function getExtraAcceptList(extraExtensions: ExtraExtension[]): string[] {
  return uniq(extraExtensions.map(getExtraAccept).filter((v) => !isEmpty(v)));
}

function getAccept(extraAcceptList: string[]) {
  const extraAccepts = extraAcceptList.join(',');
  return extraAccepts && extraAccepts.length > 0
    ? 'image/*,' + extraAccepts
    : 'image/*';
}

type Props = {
  extraExtensions?: ExtraExtension[];
  files: AttachmentWithFile[];
  helperText?: string;
  maxCount?: number;
  setFiles: (files: AttachmentWithFile[]) => void;
  sx?: SxProps<Theme>;
};

export function UploadFile(props: Props) {
  const { extraExtensions, files, maxCount = 1, setFiles, sx } = props;
  const theme = useTheme();

  const extraAcceptList = extraExtensions
    ? getExtraAcceptList(extraExtensions)
    : [];
  const accept = getAccept(extraAcceptList);

  const handleChange = async function (e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (file) {
      setFiles([
        ...files,
        {
          blob: new Blob([file], { type: file.type }),
          name: file.name,
          url: file ? URL.createObjectURL(file) : '',
        },
      ]);
    }
  };

  const handleClickDelete = useCallback(
    (key: string) => {
      setFiles([...files.filter((f: AttachmentWithFile) => f.url !== key)]);
    },
    [setFiles, files]
  );

  const isDisabled = useMemo(() => {
    return files.length >= maxCount;
  }, [files, maxCount]);

  return (
    <Box sx={sx}>
      {files.length > 0 && (
        <Stack spacing={1} my={2}>
          {files?.map((file, index) => (
            <Stack key={index} direction="row">
              <Box width={1}>
                <Typography component="span">{file?.name}</Typography>
                <Link
                  color="error"
                  ml={3}
                  onClick={() => handleClickDelete(file?.url || '')}
                >
                  削除
                </Link>
              </Box>
              <Link href={file.url} target="_blank" whiteSpace="nowrap">
                表示する
              </Link>
            </Stack>
          ))}
        </Stack>
      )}
      <Box>
        <Button
          variant="outlined"
          component="label"
          size="large"
          disabled={files.length >= maxCount}
          fullWidth
          sx={{
            borderColor: theme.palette.neutral.greyDark,
            color: theme.palette.text.secondary,
            fontWeight: 400,
          }}
        >
          <Stack direction="row" alignItems="center" width={1}>
            <UploadFileIcon sx={{ color: theme.palette.neutral.greyDark }} />
            <Typography width={1} textAlign="center">
              ファイルアップロード
            </Typography>
          </Stack>
          {!isDisabled && (
            <input
              type="file"
              hidden
              accept={accept}
              multiple
              onChange={handleChange}
            />
          )}
        </Button>
      </Box>
    </Box>
  );
}
